"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Couleurs contextuelles authentiques de Bootstrap.
const COLORS = {
    primary: { hex: "#0d6efd", solidText: "#ffffff", alertBg: "#cfe2ff", alertText: "#084298" },
    success: { hex: "#198754", solidText: "#ffffff", alertBg: "#d1e7dd", alertText: "#0f5132" },
    danger: { hex: "#dc3545", solidText: "#ffffff", alertBg: "#f8d7da", alertText: "#842029" },
    warning: { hex: "#ffc107", solidText: "#000000", alertBg: "#fff3cd", alertText: "#664d03" },
    dark: { hex: "#212529", solidText: "#ffffff", alertBg: "#ced4da", alertText: "#141619" },
};
const COLOR_KEYS = ["primary", "success", "danger", "warning", "dark"];

function cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function BsComponents() {
    const t = useTranslations();

    const [color, setColor] = useState("primary");
    const [outline, setOutline] = useState(false);
    const [large, setLarge] = useState(false);
    const [pill, setPill] = useState(false);

    const c = COLORS[color];
    const variantClass = outline ? `btn-outline-${color}` : `btn-${color}`;

    const btnStyle = {
        backgroundColor: outline ? "transparent" : c.hex,
        color: outline ? c.hex : c.solidText,
        border: `2px solid ${outline ? c.hex : "transparent"}`,
        borderRadius: pill ? 999 : 8,
        padding: large ? "12px 30px" : "8px 18px",
        fontSize: large ? 18 : 15,
        fontWeight: 600,
        transition: "all 0.25s ease",
    };

    // Groupe segmenté (2 options).
    function Segment({ value, setter, off, on, offKey, onKey }) {
        return (
            <div className="inline-flex rounded-lg bg-white/[0.04] ring-1 ring-white/10 p-0.5">
                {[
                    [off, offKey],
                    [on, onKey],
                ].map(([v, k]) => (
                    <button
                        key={String(v)}
                        type="button"
                        onClick={() => setter(v)}
                        className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition ${
                            value === v
                                ? "bg-purple-500/20 text-purple-200 ring-1 ring-purple-400/40"
                                : "text-gray-400 hover:text-gray-200"
                        }`}
                    >
                        {t(k)}
                    </button>
                ))}
            </div>
        );
    }

    return (
        <section id="bs-components" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Bs_comp_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Bs_comp_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:bootstrap"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        components.html
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Bs_comp_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8 grid lg:grid-cols-2 gap-8 items-start">
                    {/* Contrôles */}
                    <div className="min-w-0 order-2 lg:order-1 flex flex-col gap-5">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-300/90 mb-2.5">
                                {t("Bs_comp_group_color")}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {COLOR_KEYS.map((k) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setColor(k)}
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium ring-1 transition ${
                                            color === k
                                                ? "bg-white/[0.08] ring-white/40 text-white"
                                                : "bg-white/[0.03] ring-white/10 text-gray-400 hover:text-gray-200"
                                        }`}
                                    >
                                        <span
                                            className="w-3.5 h-3.5 rounded-full"
                                            style={{ backgroundColor: COLORS[k].hex }}
                                        />
                                        {cap(k)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-x-8 gap-y-4">
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-300/90 mb-2.5">
                                    {t("Bs_comp_group_style")}
                                </div>
                                <Segment
                                    value={outline}
                                    setter={setOutline}
                                    off={false}
                                    on={true}
                                    offKey="Bs_comp_style_solid"
                                    onKey="Bs_comp_style_outline"
                                />
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-300/90 mb-2.5">
                                    {t("Bs_comp_group_size")}
                                </div>
                                <Segment
                                    value={large}
                                    setter={setLarge}
                                    off={false}
                                    on={true}
                                    offKey="Bs_comp_size_normal"
                                    onKey="Bs_comp_size_large"
                                />
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-300/90 mb-2.5">
                                    {t("Bs_comp_group_shape")}
                                </div>
                                <Segment
                                    value={pill}
                                    setter={setPill}
                                    off={false}
                                    on={true}
                                    offKey="Bs_comp_shape_square"
                                    onKey="Bs_comp_shape_pill"
                                />
                            </div>
                        </div>

                        {/* Code généré */}
                        <div className="rounded-xl bg-black/40 ring-1 ring-white/10 px-4 py-3 overflow-x-auto">
                            <code className="font-mono text-[12.5px] whitespace-nowrap">
                                <span className="text-gray-500">
                                    {'<button class="'}
                                </span>
                                <span className="text-gray-300">btn </span>
                                <span style={{ color: c.hex }}>
                                    {variantClass}
                                </span>
                                {large && (
                                    <span className="text-amber-300"> btn-lg</span>
                                )}
                                {pill && (
                                    <span className="text-sky-300">
                                        {" "}
                                        rounded-pill
                                    </span>
                                )}
                                <span className="text-gray-500">{'">'}</span>
                                <span className="text-gray-200">
                                    {t("Bs_comp_btn_label")}
                                </span>
                                <span className="text-gray-500">
                                    {"</button>"}
                                </span>
                            </code>
                        </div>
                    </div>

                    {/* Aperçu */}
                    <div className="min-w-0 order-1 lg:order-2">
                        <div className="rounded-2xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/10 overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                            </div>
                            <div className="p-7 flex flex-col items-center gap-7">
                                {/* Le bouton composé */}
                                <button type="button" style={btnStyle}>
                                    {t("Bs_comp_btn_label")}
                                </button>

                                {/* Le même thème ailleurs */}
                                <div className="w-full">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 text-center mb-3">
                                        {t("Bs_comp_theme_label")}
                                    </div>
                                    <div className="flex flex-col items-center gap-3">
                                        <span
                                            style={{
                                                backgroundColor: c.hex,
                                                color: c.solidText,
                                                transition: "all 0.25s ease",
                                            }}
                                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-bold"
                                        >
                                            {t("Bs_comp_badge_text")}
                                        </span>
                                        <div
                                            style={{
                                                backgroundColor: c.alertBg,
                                                color: c.alertText,
                                                transition: "all 0.25s ease",
                                            }}
                                            className="w-full flex items-center gap-2 rounded-lg px-3.5 py-2.5 text-[13px] font-medium"
                                        >
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-4 h-4 shrink-0"
                                            />
                                            {t("Bs_comp_alert_text")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Bs_comp_caption")}
            </p>
        </section>
    );
}
